import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { HttpRequestService } from '../shared/services/http-request.service';
import { formatUrl } from '../shared/utils/format-url-util';
import { ConsumerRatesPayload, ConsumerRatesResponse } from '../interfaces/consumer-rates.model';

@Injectable({
  providedIn: 'root',
})
export class ConsumerRateService {
  constructor(
    private httpClient: HttpRequestService,
    private errorHandler: GlobalErrorHandlerService,
  ) {}

  getProviderServiceCodes(programCode: any, payload: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.GET_PROVIDER_SERVICE_CODES, {
      programCode});

    return this.httpClient.post(url, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getConsumerRatesList(programCode: string, participantId: string | number): Observable<ConsumerRatesResponse> {
    const url = formatUrl(ENDPOINTS.RATE.GET_CONSUMER_RATES_LIST, {
      programCode: programCode, participantId: participantId });
    return this.httpClient.get<ConsumerRatesResponse>(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  // Rates for a single provider under a participant. Used in provider view when the logged-in
  // lead is a service provider (IR/IC/VE/IF) rather than an Employer/Auth Rep.
  getProviderRatesList(programCode: string, participantId: string | number, providerId: string | number): Observable<ConsumerRatesResponse> {
    const url = formatUrl(ENDPOINTS.RATE.GET_PROVIDER_RATES_LIST, {
      programCode, participantId, providerId });
    return this.httpClient.get<ConsumerRatesResponse>(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getFormServiceCodes(programCode: string, participantId: string, providerId: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.GET_FORM_SERVICE_CODES, {
      programCode, participantId, providerId });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  createProviderServiceRates(programCode: string, payload: ConsumerRatesPayload): Observable<ConsumerRatesResponse> {
    const url = formatUrl(ENDPOINTS.RATE.POST_PROVIDER_SERVICE_RATES, { programCode });
    return this.httpClient.post<ConsumerRatesResponse>(url, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  updateProviderServiceRates(programCode: string, payload: ConsumerRatesPayload): Observable<ConsumerRatesResponse> {
    const url = formatUrl(ENDPOINTS.RATE.PUT_PROVIDER_SERVICE_RATES, { programCode });
    return this.httpClient.put<ConsumerRatesResponse>(url, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addConsumerRate(programCode: any, formData: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.POST_CONSUMER_RATES, { programCode });
    return this.httpClient.post(url, formData).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  updateConsumerRate(programCode: any, rateId: any, formData: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.PUT_CONSUMER_RATES, { programCode, rateId });
    return this.httpClient.put(url, formData).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  /**
   * Deletes provider rates in one call. Both ids and their premium rates go out as
   * comma-separated lists, so a multi-row removal is a single request rather than one per row.
   */
  deleteProviderRates(programCode: any, rateIds: Array<string | number>, premiumRates: Array<string | number>): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.DEACTIVATE, { programCode });
    const rateIdsToDelete = [...rateIds, ...premiumRates];
    
    const payload = {
      ids: rateIdsToDelete
    };

    return this.httpClient.post(url, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }
  getMinimumRate(programCode: any, pplId: any, startDate: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.GET_MINIMUM_RATE, { programCode, pplId, startDate });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      })
    );
  }

  getServiceCodeRate(programCode: any, serviceCode: any, serviceDate: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.GET_SERVICE_CODE_RATE, { programCode, serviceCode, serviceDate });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      })
    );
  }

  patchRateProhibitionFlag(programCode: any, rateIds: any[]): Observable<any> {
    const url = formatUrl(ENDPOINTS.RATE.PATCH_RATE_PROHIBITION_FLAG, { programCode });
    return this.httpClient.patch(url, { rateIds: rateIds }).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      })
    );
  }
}
